/**
 * The spatiotemporal matrix: (frequency x time) -> what is on the air.
 *
 * This is the piece that makes the band feel like a place rather than a menu. A transmission
 * exists at a frequency, during a window, and possibly drifts while it is there — so finding one
 * is an act of search in two dimensions, and losing one is a real loss.
 *
 * Three rules it enforces:
 *
 * 1. **One active transmitter per band.** Two signals must never be audible at the same dial
 *    position at the same moment. The validator catches that at authoring time; this resolver is
 *    the runtime backstop, and it resolves deterministically (priority, then id) so the same
 *    world never sounds different twice.
 * 2. **Schedules are data.** Windows are expressed in game time, real time, or both, and the
 *    resolver reads them without knowing what any particular signal *is*.
 * 3. **Drift is a function of time, not of state.** A signal's frequency is computed from the
 *    clock, never accumulated, so it is identical after a reload and cheap to evaluate for any
 *    moment — including moments the player has not reached yet, which is what lets the validator
 *    sweep a whole night for collisions.
 */
import { MS_PER_GAME_DAY, gameTimeParts, hourInWindow, type WorldTime } from './clock.js';
import type { PackEffects, PackGate, PackRoaming } from '../content/schema.js';
import { isTransmittingAt, stationAt } from './roaming.js';

const MS_PER_HOUR = 60 * 60 * 1000;

/**
 * How far out a signal is still audible, as a multiple of its width.
 *
 * The tuning curve is `exp(-1.6 * (delta/width)^2)` (audio/tuning.ts); it falls to 5% — the point
 * where a transmission stops being anything a player could notice under the noise floor — at
 * about 1.37 widths. Collision detection uses the same number, so "these two overlap" means the
 * same thing to the validator as it does to the player's ears.
 */
export const AUDIBLE_WIDTHS = 1.37;

export type SignalKind = 'story' | 'parasite' | 'npc' | 'ghost';

/** What the audio layer should play. Chapter 3 adds authored dialogue nodes on top of this. */
export type SignalSource =
  | {
      type: 'synth';
      /** A generative preset — parasite FX and placeholders need no assets (locked decision). */
      preset: string;
      params?: Record<string, number>;
    }
  | {
      type: 'audio';
      /** Resolved to `audio/<locale>/<npc>/<node>.ogg` by the content loader. */
      url: string;
      loop?: boolean;
    };

export interface SignalPlacement {
  /** Where on the dial the signal sits when its window opens, in kHz. */
  frequencyKhz: number;
  /** Half-width of the approach, in kHz — the same parameter the tuning DSP takes. */
  widthKhz: number;
  /**
   * Movement across the band while the window is open. Anchored to the window's start by
   * default, so a nightly signal drifts the same way each night rather than wandering off
   * forever.
   */
  drift?: {
    khzPerGameHour: number;
    anchor?: 'window' | 'world';
    /** Optional bounds; the signal reverses at the edges rather than leaving the band. */
    minKhz?: number;
    maxKhz?: number;
  };
  /**
   * An NPC working a set of stations (Chapter 6). Takes precedence over `frequencyKhz` and
   * `drift` — an operator moving between frequencies is doing something different from a signal
   * sliding across the band, and mixing the two would be neither.
   */
  roaming?: PackRoaming;
  /** The id whose hash seeds the station choice. Set by the loader to the NPC's id. */
  roamingSeed?: string;
}

/** A daily-recurring in-game window. Wraps midnight, because nights do. */
export interface GameHourWindow {
  fromHour: number;
  toHour: number;
}

/** An absolute, one-off span of game time. */
export interface GameSpan {
  fromGameMs: number;
  toGameMs: number;
}

/** Real-world anchors — the few signals tied to the player's actual clock and calendar. */
export interface RealAnchor {
  /** Local wall-clock hour window, exclusive at `to`. Wraps midnight. */
  hours?: { from: number; to: number };
  /** Local weekdays, 0 = Sunday. */
  weekdays?: number[];
  /** An absolute real-world date range, ISO 8601. For one-time events. */
  dateRange?: { fromIso: string; toIso: string };
}

/**
 * When a signal is on the air. Every clause present must match — they are ANDed — and a schedule
 * with no clauses at all is always on, which is what a permanent background parasite wants.
 */
export interface Schedule {
  /** Daily-recurring game-time windows. Any one matching is enough. */
  gameHours?: GameHourWindow[];
  /** Restrict to particular in-game days (day 1 is the first night). */
  gameDays?: number[];
  /** An absolute one-off game-time span. */
  gameSpan?: GameSpan;
  /** Real-world anchoring. */
  real?: RealAnchor;
}

export interface ScheduledSignal {
  id: string;
  kind: SignalKind;
  placement: SignalPlacement;
  schedule: Schedule;
  /**
   * Higher wins when two signals would occupy the same band at the same time. This is a
   * tie-break for authoring mistakes, not a licence to stack signals — the validator still
   * reports the overlap.
   */
  priority?: number;
  source: SignalSource;
  /** Locale-resolved display label, for the frequency log. */
  label?: string;
  /** Locale-resolved description of what this sounds like, for captions (Chapter 7). */
  caption?: string;

  /* --- Chapter 3 story hooks ------------------------------------------------------------- */
  /**
   * Gate on the player's story state, ANDed with the schedule. The matrix does not evaluate
   * this — it has no idea what a clue is — so the caller filters on it. Keeping it here means a
   * gated signal still travels as one object.
   */
  requires?: PackGate;
  /** Granted once the player has actually held this signal for `listenSeconds`. */
  effects?: PackEffects;
  listenSeconds?: number;
  /** Conversation this signal carries. */
  dialogue?: string;

  /* --- Chapter 6 NPC hooks --------------------------------------------------------------- */
  /** Set when this signal *is* an NPC, so the UI can name who is speaking. */
  npcId?: string;
  /**
   * Only on air when the band is quiet enough. Evaluated by the caller, which is the only place
   * that knows how many people are actually listening.
   */
  coverage?: { maxOthers: number };
}

/** A signal that is currently on the air, with its frequency resolved for this moment. */
export interface ActiveSignal {
  signal: ScheduledSignal;
  /** Where it actually is right now, after drift. */
  frequencyKhz: number;
  widthKhz: number;
  /** Game time at which its current window opened — the drift anchor. */
  windowStartGameMs: number;
}

/** Start of the in-game day containing `gameMs`. */
function startOfGameDay(gameMs: number): number {
  return Math.floor(gameMs / MS_PER_GAME_DAY) * MS_PER_GAME_DAY;
}

/**
 * The most recent opening of a daily window at or before `gameMs`, assuming the window is
 * currently open. Midnight-wrapping windows opened yesterday.
 */
function windowStartFor(gameMs: number, fromHour: number): number {
  const candidate = startOfGameDay(gameMs) + fromHour * MS_PER_HOUR;
  return candidate <= gameMs ? candidate : candidate - MS_PER_GAME_DAY;
}

function matchesReal(anchor: RealAnchor, time: WorldTime): boolean {
  if (anchor.hours && !hourInWindow(time.realHour, anchor.hours.from, anchor.hours.to)) {
    return false;
  }
  if (anchor.weekdays && !anchor.weekdays.includes(time.realWeekday)) return false;
  if (anchor.dateRange) {
    const from = Date.parse(anchor.dateRange.fromIso);
    const to = Date.parse(anchor.dateRange.toIso);
    if (Number.isFinite(from) && time.realMs < from) return false;
    if (Number.isFinite(to) && time.realMs >= to) return false;
  }
  return true;
}

/**
 * Is this schedule open at `time`, and if so when did the current window start?
 * The window start is returned because drift is measured from it.
 */
export function resolveSchedule(
  schedule: Schedule,
  time: WorldTime,
): { active: boolean; windowStartGameMs: number } {
  const inactive = { active: false, windowStartGameMs: 0 };

  if (schedule.real && !matchesReal(schedule.real, time)) return inactive;

  if (schedule.gameDays && !schedule.gameDays.includes(time.game.day)) return inactive;

  if (schedule.gameSpan) {
    const { fromGameMs, toGameMs } = schedule.gameSpan;
    if (time.gameMs < fromGameMs || time.gameMs >= toGameMs) return inactive;
  }

  if (schedule.gameHours && schedule.gameHours.length > 0) {
    const open = schedule.gameHours.find((window) =>
      hourInWindow(time.game.hour, window.fromHour, window.toHour),
    );
    if (!open) return inactive;
    return { active: true, windowStartGameMs: windowStartFor(time.gameMs, open.fromHour) };
  }

  // No game-time clause: the window is whatever the other clauses allow, anchored at the span
  // start if there is one, otherwise at the start of the world.
  return { active: true, windowStartGameMs: schedule.gameSpan?.fromGameMs ?? 0 };
}

/** Fold a drifting frequency back into its bounds, reversing at the edges. */
function reflect(value: number, min: number, max: number): number {
  if (max <= min) return min;
  const span = max - min;
  const offset = (((value - min) % (2 * span)) + 2 * span) % (2 * span);
  return min + (offset <= span ? offset : 2 * span - offset);
}

/** Where a signal sits on the dial at a given moment. */
export function signalFrequencyAt(
  signal: ScheduledSignal,
  gameMs: number,
  windowStartGameMs: number,
): number {
  const { frequencyKhz, drift, roaming, roamingSeed } = signal.placement;

  // A roaming NPC is *at* a station, not drifting toward one.
  if (roaming && roaming.stations.length > 0) {
    return stationAt(roamingSeed ?? signal.id, roaming, gameMs);
  }

  if (!drift || drift.khzPerGameHour === 0) return frequencyKhz;

  const anchorMs = drift.anchor === 'world' ? 0 : windowStartGameMs;
  const hours = (gameMs - anchorMs) / MS_PER_HOUR;
  const drifted = frequencyKhz + drift.khzPerGameHour * hours;

  if (drift.minKhz === undefined && drift.maxKhz === undefined) return drifted;
  const min = drift.minKhz ?? Number.NEGATIVE_INFINITY;
  const max = drift.maxKhz ?? Number.POSITIVE_INFINITY;
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return Math.min(max, Math.max(min, drifted));
  }
  return reflect(drifted, min, max);
}

/** How far from a signal's centre it can still be heard. */
export function audibleHalfWidthKhz(widthKhz: number): number {
  return widthKhz * AUDIBLE_WIDTHS;
}

/** Do two placed signals overlap on the dial? */
export function overlaps(a: ActiveSignal, b: ActiveSignal): boolean {
  const distance = Math.abs(a.frequencyKhz - b.frequencyKhz);
  return distance < audibleHalfWidthKhz(a.widthKhz) + audibleHalfWidthKhz(b.widthKhz);
}

/** Deterministic ordering: priority first, then id, so a tie never resolves two ways. */
function byPrecedence(a: ScheduledSignal, b: ScheduledSignal): number {
  const priority = (b.priority ?? 0) - (a.priority ?? 0);
  return priority !== 0 ? priority : a.id.localeCompare(b.id);
}

export interface BandConflict {
  aId: string;
  bId: string;
  frequencyKhz: number;
  gameMs: number;
}

/**
 * The resolver. Built once from a content pack's signals and asked what is on the air.
 */
export class SignalMatrix {
  private readonly signals: ScheduledSignal[];

  constructor(signals: readonly ScheduledSignal[]) {
    // Sorted once so every resolution walks them in the same order.
    this.signals = [...signals].sort(byPrecedence);
  }

  get size(): number {
    return this.signals.length;
  }

  all(): readonly ScheduledSignal[] {
    return this.signals;
  }

  /**
   * Every signal on the air at this moment, with its drifted frequency.
   *
   * `suppressed` holds signals whose window is open but which lose a band to a
   * higher-precedence transmitter — the "one transmitter per band" rule, applied at runtime.
   *
   * `eligible` filters signals *before* precedence is applied, which is the only correct place
   * for it. Chapter 3 gates signals on story state, and a locked signal that still took part in
   * the precedence contest would mask everything beneath it on its band: the player would hear
   * dead air where an unlocked, lower-priority transmitter was in fact broadcasting. A signal the
   * player cannot receive is not a quiet transmitter — it is not there at all.
   */
  activeAt(
    time: WorldTime,
    eligible?: (signal: ScheduledSignal) => boolean,
    adapt?: (signal: ScheduledSignal, time: WorldTime) => ScheduledSignal,
  ): { active: ActiveSignal[]; suppressed: ActiveSignal[] } {
    const scheduled: ActiveSignal[] = [];
    for (const original of this.signals) {
      if (eligible && !eligible(original)) continue;
      // Chapter 6: an NPC on a task is somewhere specific. Applied here, before precedence, so a
      // tasked NPC contests its band exactly like any other transmitter.
      const signal = adapt ? adapt(original, time) : original;
      const { active, windowStartGameMs } = resolveSchedule(signal.schedule, time);
      if (!active) continue;
      // A roaming NPC is only on the air while it is actually keyed up. Between bursts it is
      // listening, and the band it is sitting on is genuinely free — which is what lets two NPCs
      // share a station at different moments without either being suppressed.
      const roaming = signal.placement.roaming;
      if (roaming && !isTransmittingAt(roaming, time.gameMs)) continue;
      scheduled.push({
        signal,
        frequencyKhz: signalFrequencyAt(signal, time.gameMs, windowStartGameMs),
        widthKhz: roaming?.widthKhz ?? signal.placement.widthKhz,
        windowStartGameMs,
      });
    }

    const active: ActiveSignal[] = [];
    const suppressed: ActiveSignal[] = [];
    for (const candidate of scheduled) {
      if (active.some((held) => overlaps(held, candidate))) suppressed.push(candidate);
      else active.push(candidate);
    }
    return { active, suppressed };
  }

  /**
   * What the receiver would pick up at this dial position: the nearest signal whose audible
   * range covers it, or null for dead air. Ties go to the closer centre — a stronger signal
   * masks a weaker one, which is also what a real receiver does.
   */
  resolveAtFrequency(
    frequencyKhz: number,
    time: WorldTime,
    eligible?: (signal: ScheduledSignal) => boolean,
    adapt?: (signal: ScheduledSignal, time: WorldTime) => ScheduledSignal,
  ): ActiveSignal | null {
    let best: ActiveSignal | null = null;
    let bestDistance = Infinity;
    for (const candidate of this.activeAt(time, eligible, adapt).active) {
      const distance = Math.abs(candidate.frequencyKhz - frequencyKhz);
      if (distance >= audibleHalfWidthKhz(candidate.widthKhz)) continue;
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    }
    return best;
  }

  /**
   * Every band conflict this matrix would produce over a sweep of moments — the validator's
   * collision check. Sampling rather than solving analytically is deliberate: drift, wrapping
   * windows and real-world anchors make a closed form fragile, and a fine sweep over a few
   * in-game days is both exact enough to catch real authoring mistakes and fast.
   */
  conflicts(times: readonly WorldTime[]): BandConflict[] {
    const seen = new Set<string>();
    const found: BandConflict[] = [];

    for (const time of times) {
      const { active, suppressed } = this.activeAt(time);
      for (const loser of suppressed) {
        const winner = active.find((held) => overlaps(held, loser));
        if (!winner) continue;
        const key = [winner.signal.id, loser.signal.id].sort().join(' ');
        if (seen.has(key)) continue;
        seen.add(key);
        found.push({
          aId: winner.signal.id,
          bId: loser.signal.id,
          frequencyKhz: Math.round((winner.frequencyKhz + loser.frequencyKhz) / 2),
          gameMs: time.gameMs,
        });
      }
    }
    return found;
  }
}

/**
 * A sweep of world-times for the validator to check: every `stepMs` of game time across `days`,
 * with the real clock held at a fixed moment.
 *
 * Real-world-anchored schedules are checked separately by the validator (it re-sweeps with the
 * real clock set to each anchored hour), because sweeping real time here would multiply the
 * sample count for no gain on the majority of signals that ignore it.
 */
export function sweepGameTimes(options: {
  days: number;
  stepMs: number;
  startGameMs?: number;
  realMs?: number;
  realHour?: number;
  realWeekday?: number;
}): WorldTime[] {
  const { days, stepMs } = options;
  const startGameMs = options.startGameMs ?? 0;
  const realMs = options.realMs ?? 0;
  const times: WorldTime[] = [];
  const total = days * MS_PER_GAME_DAY;

  for (let offset = 0; offset < total; offset += stepMs) {
    const gameMs = startGameMs + offset;
    const game = gameTimeParts(gameMs);
    times.push({
      gameMs,
      game,
      realMs,
      realHour: options.realHour ?? game.hour,
      realWeekday: options.realWeekday ?? 0,
    });
  }
  return times;
}
